'use client';

import { Button, Grid, TextLink, TextPassage } from '@petsmart-ui/sparky';
import { Form, Formik, FormikErrors } from 'formik';
import { useState } from 'react';

import { trackError } from '@libs/analytics';
import type { InterstitialCta, InterstitialLinkAction } from '../InterstitialSignIn';
import { PasswordField } from '../SignInForm/PasswordField';
import { validateSignInFormSoft } from './schema';
import type { SignInFormSoftValues } from './types';

import { logoutUser } from '@libs/core/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { KeepSignedInCheckbox } from '../KeepSignedInCheckbox';
import { ForgotPasswordCta } from '../SignInForm/ForgotPasswordCta';
import './SignInFormSoft.scss';

import { SIGN_IN_PAGE } from '../SignInForm/constants';
import { windowRedirect } from '../../utils';

const initialFormState: SignInFormSoftValues = {
    password: '',
};

export type SignInFormSoftProps = {
    cta?: InterstitialCta;
    linkAction?: InterstitialLinkAction;
    isInDrawer?: boolean;
};

const componentClassname = 'sign-in-form-soft';

export function SignInFormSoft({ linkAction, isInDrawer = false }: Readonly<SignInFormSoftProps>) {
    const [isFetching, setIsFetching] = useState(false);

    const pathname = usePathname();
    const searchParams = useSearchParams();
    const redirectUrl = searchParams?.get('redirectUrl') ?? null;

    const idPrefix = isInDrawer ? 'sign-in-drawer-soft-' : '';

    const userEmailTip = 'ema***';

    function handleInvalidSubmit({ password }: { password?: string[] }) {
        const errorMessage = [...(password ?? [])];
        trackError('login', errorMessage.join(' | '));
    }

    async function handleValidateForm(validateForm: (values?: unknown) => Promise<FormikErrors<SignInFormSoftValues>>) {
        console.log('GM: Validate Form');
        const result = await validateForm();
        if (Object.keys(result).length > 0) {
            handleInvalidSubmit(
                result as {
                    [key: string]: string[];
                }
            );
        }
    }

    async function handleSubmitForm() {
        setIsFetching(true);
        // implement new login flow here
        setIsFetching(false);
        handleRedirect();
    }

    function handleRedirect() {
        if (pathname === SIGN_IN_PAGE && redirectUrl) {
            // can be replaced with router.push after completing AC-4626
            windowRedirect(redirectUrl);
        }
    }

    function handleLogout() {
        logoutUser('sign-in');
    }

    return (
        <Formik
            onSubmit={handleSubmitForm}
            initialValues={initialFormState}
            validate={validateSignInFormSoft}
            validateOnBlur
        >
            {({ validateForm, handleSubmit }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <Grid gap="md">
                        <Grid.Item className={`${componentClassname}__fields`}>
                            <Grid.Item className={`${componentClassname}__identify`}>
                                <TextPassage
                                    data-testid="email-tip"
                                    className={`${componentClassname}__identify--email`}
                                >
                                    {userEmailTip}
                                </TextPassage>
                                <TextPassage>Not you?</TextPassage>
                                <TextLink href="/sign-in" data-testid="sign-out-cta" onClick={handleLogout}>
                                    Sign out
                                </TextLink>
                            </Grid.Item>

                            <PasswordField data-testid="testingId" uniqueId={`${idPrefix}password`} />

                            <KeepSignedInCheckbox />
                        </Grid.Item>

                        <Grid.Item className={`${componentClassname}__cta`}>
                            <Button
                                data-testid="signInCta"
                                fullWidth
                                hideText={isFetching}
                                loading={isFetching}
                                text="Sign in"
                                type="submit"
                                onClick={async () => await handleValidateForm(validateForm)}
                                size="lg"
                                variant="primary"
                            />
                        </Grid.Item>

                        <ForgotPasswordCta
                            componentClassname={componentClassname}
                            webUrl={linkAction?.fields?.link?.webUrl}
                            actionText={linkAction?.fields?.actionText}
                        />
                    </Grid>
                </Form>
            )}
        </Formik>
    );
}
